import { Injectable } from '@angular/core';
import { Subject, of, Observable } from 'rxjs';
import { withLatestFrom, switchMap, startWith } from 'rxjs/operators';
import { getDashboardCombined } from '../../reducers';
import { State } from '../../reducers/dashboard/reducers-dashboard';
import { turnoverOptions, dateTypeOptions, valueOptions } from '../../models/dashboard/option-values';
import { Store, select } from '@ngrx/store';
import { getFilterBySymbol } from '../utils/data-helpers';
import { camelize } from '../utils/data-helpers';

import * as json2csv from 'json2csv'; // convert json file to csv
import { saveAs } from 'file-saver'; // save the file

/**
 * Converts dashboard state into csv data, as array of strings
 */
@Injectable()
export class CsvService {
  public Json2csvParser = json2csv.Parser;
  public camelize = camelize;

  private readonly trigger: Subject<any>;
  private readonly result$: Observable<string[][]>;
  private filterBySymbol = '';

  constructor(private readonly store: Store<State>) {
    this.trigger = new Subject();
    this.result$ = this.trigger.pipe(
      startWith(true),
      withLatestFrom(this.store.pipe(select(getDashboardCombined))),
      switchMap(([_, dashboardState]) => {
        this.filterBySymbol = getFilterBySymbol(dashboardState);

        const csvData = [...this.getOptionsData(dashboardState)];

        if (dashboardState.report.data.bookingReport.bookings.length) {
          csvData.push([], ...this.getBookingData(dashboardState));
        }
        if (dashboardState.report.data.companyReports && dashboardState.report.data.companyReports.length) {
          csvData.push([], ...this.getCarrierData(dashboardState));
        }

        return of(csvData);
      })
    );
  }

  // subscribe on result to get updated file
  public getResult(): Observable<string[][]> {
    return this.result$;
  }

  // call update when you need to generate csv data for current values
  public update() {
    this.trigger.next(undefined);
  }

  public getFileName(): string {
    const d = new Date();
    const dParts = [d.getDate(), d.getMonth() + 1, d.getFullYear()];
    return dParts.join('_') + '_AmadeusDirect_dashboard.csv';
  }

  public downloadFile(data: any, filename?: string) {
    const csvData = this.convertToCSV(data);
    const file = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
    saveAs(file, filename);
  }

  public convertToCSV(objArray: any, fields?) {
    const json2csvParser = new this.Json2csvParser({ opts: fields });
    return json2csvParser.parse(objArray);
  }

  private getOptionsData(dashboardState: State): string[][] {
    const titles: string[] = [];
    const values: string[] = [];
    // Filtered By
    const dashboardFilteredby = $localize`:@@csv.service.dashboardFilteredby:Filtered By`;
    titles.push(dashboardFilteredby);
    const turnoverOption = turnoverOptions.find(({ value }) => value === dashboardState.options.filters.turnover);

    values.push(turnoverOption.label);
    // Data Type
    const dashboardOptionsDate = $localize`:@@csv.service.dashboardOptionsDate:Date Type`;
    titles.push(dashboardOptionsDate);
    const dateTypeOption = dateTypeOptions.find(({ value }) => value === dashboardState.options.filters.dateType);
    values.push(dateTypeOption.label);
    // Start date
    const dashboardOptionsStartDate = $localize`:@@csv.service.dashboardOptionsStartDate:Start date`;
    titles.push(dashboardOptionsStartDate);
    values.push(
      dashboardState.options.filters.dateFrom
        .split('-')
        .reverse()
        .join('/')
    );
    // End date
    const dashboardOptionsEndDate = $localize`:@@csv.service.dashboardOptionsEndDate:End date`;
    titles.push(dashboardOptionsEndDate);
    values.push(
      dashboardState.options.filters.dateTo
        .split('-')
        .reverse()
        .join('/')
    );
    // Total Revenue
    const dashboardBaseTurnover = $localize`:@@csv.service.dashboardBaseTurnover:Total Revenue`;
    titles.push(dashboardBaseTurnover);
    const filterByOption = valueOptions.find(({ value }) => value === dashboardState.options.filterBy);
    const filterByValue = filterByOption.label;
    values.push('value' === filterByOption.value ? filterByValue + ' ' + this.filterBySymbol : filterByValue);
    // Tickets Issued
    const dashboardTicketsissued = $localize`:@@csv.service.dashboardTicketsissued:Tickets Issued`;
    titles.push(dashboardTicketsissued);
    values.push(dashboardState.report.data.bookingReport.ticketsIssued.toString());
    // Net Bookings
    const dashboardNetbookings = $localize`:@@csv.service.dashboardNetbookings:Net Bookings`;
    titles.push(dashboardNetbookings);
    values.push(dashboardState.report.data.bookingReport.netBookings.toString());
    // Revenues
    const dashboardRevenues = $localize`:@@csv.service.dashboardRevenues:Revenues`;
    titles.push(dashboardRevenues);
    values.push(dashboardState.report.data.bookingReport.revenues.toString());
    return [titles, values];
  }

  private getBookingData(dashboardState: State): any[][] {
    const dashboardOptionsDate = $localize`:@@csv.service.dashboardOptionsDate:Date Type`;
    const dashboardCompleted = $localize`:@@csv.service.dashboardCompleted:Completed`;
    const dashboardTicketed = $localize`:@@csv.service.dashboardTicketed:Ticketed`;
    const dashboardUnticketed = $localize`:@@csv.service.dashboardUnticketed:Unticketed`;
    const dashboardTotal = $localize`:@@csv.service.dashboardTotal:Total`;
    const titles = [
      dashboardOptionsDate, // Data Type
      dashboardCompleted, // Completed
      dashboardTicketed, // Ticketed
      dashboardUnticketed, // Unticketed
      dashboardTotal // Total %symbol%
    ];
    titles[4] += ' ' + this.filterBySymbol;

    const values = dashboardState.report.data.bookingReport.bookings.map(booking => [
      booking.date,
      booking.values.completed,
      booking.values.ticketed,
      booking.values.unticketed,
      booking.total
    ]);

    return [titles, ...values];
  }

  private getCarrierData(dashboardState: State): any[][] {
    const dashboardTitle: string = $localize`:@@csv.service.dashboardTitle:Title`;
    const dashboardValue: string = $localize`:@@csv.service.dashboardValue:Value`;
    const dashboardPercentage: string = $localize`:@@csv.service.dashboardPercentage:Percentage`;

    const titles = [
      dashboardTitle, // Title
      dashboardValue, // Value
      dashboardPercentage // Percentage
    ];
    const detailKeys = dashboardState.report.data.companyReports[0].details.map(detail => Object.keys(detail)[0]);

    titles.push(...detailKeys.map(key => this['dashboardDetails' + camelize(key)]));
    const values = dashboardState.report.data.companyReports.map(record =>
      [record.title, record.value, record.percentage].concat(record.details.map(detail => Object.values(detail)[0]))
    );

    return [titles.map(v => v), ...values];
  }
}
